import { useInfiniteQuery } from "@tanstack/react-query";
import { isTileProcessing } from "@/components/plans/tiled-plan-viewer/tiled-plan-viewer.helpers";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { OFFLINE_DURABLE_GC_TIME, planKeys } from "@/lib/query-keys";
import { isPinnedSync } from "@/offline/pins";
import type { PaginatedResponse, ProjectPlan } from "@/types/api";

const PAGE_SIZE = 100;

export const TILE_POLL_INTERVAL_MS = 5_000;
/** Stop hammering after 5 min of a plan staying pending/processing. */
export const TILE_POLL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * planId → epoch ms when we first saw it pending/processing. Module-level on
 * purpose: refetchInterval callbacks run per query instance and must share
 * the give-up clock across remounts. Entries are pruned the moment a plan
 * leaves the pending set, so a later retile gets a fresh window.
 */
const tilePollFirstSeen = new Map<string, number>();

/** Re-arm polling for one plan (used by the retile mutation). */
export function resetTilePollTimer(planId: string): void {
  tilePollFirstSeen.delete(planId);
}

export function __resetTilePollStateForTesting(): void {
  tilePollFirstSeen.clear();
}

// Resume on window focus: clearing the clocks re-arms every timed-out plan;
// TanStack's own refetchOnWindowFocus fires the immediate refetch and the
// next interval evaluation starts a fresh 5min window.
if (typeof window !== "undefined") {
  const onFocus = () => tilePollFirstSeen.clear();
  window.addEventListener("focus", onFocus);
  // HMR hygiene: drop this module instance's listener when Vite swaps the
  // module — otherwise every hot update stacks another one. (The clock map
  // itself needs no guard: a fresh instance just re-seeds on the next
  // interval evaluation.)
  import.meta.hot?.dispose(() => window.removeEventListener("focus", onFocus));
}

/**
 * The refetchInterval policy. Deterministic but NOT pure — it seeds and
 * prunes the module-level give-up clocks for the plans it is shown;
 * extracted from the hook so unit tests can drive it directly.
 */
export function tilePollInterval(
  plans: ProjectPlan[] | undefined,
): number | false {
  if (!plans || plans.length === 0) return false;
  const now = Date.now();
  let pendingCount = 0;
  let anyInsideWindow = false;
  for (const plan of plans) {
    if (!isTileProcessing(plan.latest_revision)) {
      // Evidence this plan left the pending set — clear its clock so a
      // later retile gets a fresh 5-min window. Scoped to plans THIS query
      // can see: a whole-map prune here would wipe the clocks of any other
      // active plans query (two projects open at once) and disable their
      // give-up forever.
      tilePollFirstSeen.delete(plan.id);
      continue;
    }
    pendingCount += 1;
    const firstSeen = tilePollFirstSeen.get(plan.id) ?? now;
    if (!tilePollFirstSeen.has(plan.id)) tilePollFirstSeen.set(plan.id, now);
    if (now - firstSeen <= TILE_POLL_TIMEOUT_MS) anyInsideWindow = true;
  }
  if (pendingCount === 0) return false;
  return anyInsideWindow ? TILE_POLL_INTERVAL_MS : false;
}

export function useProjectPlans(projectId: string) {
  const fetchWithAuth = useAuthenticatedFetch();

  return useInfiniteQuery({
    queryKey: planKeys.list(projectId),
    queryFn: ({ pageParam }) =>
      fetchWithAuth<PaginatedResponse<ProjectPlan>>(
        `/projects/1.0/projects/${projectId}/plans/?page=${pageParam}&page_size=${PAGE_SIZE}`,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.next ? lastPageParam + 1 : undefined,
    select: (data) => ({
      plans: data.pages.flatMap((page) => page.results),
      totalCount: data.pages[0]?.count ?? 0,
    }),
    staleTime: 30_000,
    // NB: receives raw InfiniteData (select doesn't apply here).
    refetchInterval: (query) =>
      tilePollInterval(query.state.data?.pages.flatMap((page) => page.results)),
    // GC-immune only while pinned — mirrors what the persister saves for this
    // project. Non-pinned projects keep the default gcTime.
    gcTime: isPinnedSync(projectId) ? OFFLINE_DURABLE_GC_TIME : undefined,
  });
}
